import React, { useState } from 'react';
import {
  ThunderboltOutlined,
  SafetyOutlined,
  AimOutlined,
  GlobalOutlined,
  MailOutlined,
  EnvironmentOutlined,
} from '@ant-design/icons';
import { useLanguage } from '../components/language/LanguageContext';
import styles from './ContactSection.module.scss';

const ContactSection: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'service' | 'support'>('service');
  const { t } = useLanguage();

  return (
    <section id="contact-section" className={styles.contactSection}>
      <div className={styles.container}>
        {/* Part 1: Value Proposition */}
        <div className={styles.valueProposition}>
          <div className={styles.valueCard}>
            <div className={styles.cardContent}>
              {/* Left Content - Features & Service Description */}
              <div className={styles.contentArea}>
                <div className={styles.header}>
                  <h2 className={styles.headline}>{t('landing.contact.headline')}</h2>
                  <p className={styles.description}>{t('landing.contact.description')}</p>
                </div>

                <div className={styles.featuresGrid}>
                  {/* Feature 1 */}
                  <div className={styles.feature}>
                    <div className={styles.featureIcon}>
                      <ThunderboltOutlined />
                    </div>
                    <div>
                      <h4 className={styles.featureTitle}>
                        {t('landing.contact.features.speed.title')}
                      </h4>
                      <p className={styles.featureDesc}>
                        {t('landing.contact.features.speed.desc')}
                      </p>
                    </div>
                  </div>

                  {/* Feature 2 */}
                  <div className={styles.feature}>
                    <div className={styles.featureIcon}>
                      <SafetyOutlined />
                    </div>
                    <div>
                      <h4 className={styles.featureTitle}>
                        {t('landing.contact.features.verify.title')}
                      </h4>
                      <p className={styles.featureDesc}>
                        {t('landing.contact.features.verify.desc')}
                      </p>
                    </div>
                  </div>

                  {/* Feature 3 */}
                  <div className={styles.feature}>
                    <div className={styles.featureIcon}>
                      <AimOutlined />
                    </div>
                    <div>
                      <h4 className={styles.featureTitle}>
                        {t('landing.contact.features.accuracy.title')}
                      </h4>
                      <p className={styles.featureDesc}>
                        {t('landing.contact.features.accuracy.desc')}
                      </p>
                    </div>
                  </div>

                  {/* Feature 4 */}
                  <div className={styles.feature}>
                    <div className={styles.featureIcon}>
                      <GlobalOutlined />
                    </div>
                    <div>
                      <h4 className={styles.featureTitle}>
                        {t('landing.contact.features.industry.title')}
                      </h4>
                      <p className={styles.featureDesc}>
                        {t('landing.contact.features.industry.desc')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Visual/Testimonial */}
              <div className={styles.testimonialArea}>
                <div className={styles.decorCircle1}></div>
                <div className={styles.decorCircle2}></div>

                <div className={styles.testimonial}>
                  <blockquote className={styles.quote}>
                    "{t('landing.contact.quote')}"
                  </blockquote>
                  <div className={styles.author}>
                    <div className={styles.avatar}>RB</div>
                    <div>
                      <div className={styles.authorName}>{t('landing.contact.teamName')}</div>
                      <div className={styles.authorRole}>{t('landing.contact.teamRole')}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Part 2: Contact Form */}
        <div className={styles.contactForm}>
          {/* Contact Info Side */}
          <div className={styles.contactInfo}>
            <div>
              <h2 className={styles.formHeadline}>{t('landing.contact.formHeader')}</h2>
              <p className={styles.formSubheadline}>{t('landing.contact.formSub')}</p>
            </div>

            <div className={styles.contactDetails}>
              <div className={styles.contactItem}>
                <div className={styles.contactIconWrapper}>
                  <EnvironmentOutlined />
                </div>
                <div>
                  <p className={styles.contactLabel}>{t('landing.contact.headquarters')}</p>
                  <p className={styles.contactValue}>San Francisco Bay Area, CA 95014</p>
                </div>
              </div>

              <div className={styles.contactItem}>
                <div className={styles.contactIconWrapper}>
                  <MailOutlined />
                </div>
                <div>
                  <p className={styles.contactLabel}>{t('landing.contact.emailUs')}</p>
                  <a href="mailto:info@roundblock.io" className={styles.contactLink}>
                    info@roundblock.io
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Form Side */}
          <div className={styles.formArea}>
            {/* Tabs */}
            <div className={styles.tabs}>
              <button
                onClick={() => setActiveTab('service')}
                className={`${styles.tab} ${activeTab === 'service' ? styles.activeTab : ''}`}
              >
                {t('landing.contact.tabService')}
              </button>
              <button
                onClick={() => setActiveTab('support')}
                className={`${styles.tab} ${activeTab === 'support' ? styles.activeTab : ''}`}
              >
                {t('landing.contact.tabSupport')}
              </button>
            </div>

            <form>
              <div className={styles.formRow}>
                <div className={styles.formField}>
                  <label className={styles.label}>{t('landing.contact.labels.name')}</label>
                  <input
                    type="text"
                    className={styles.input}
                    placeholder={t('landing.contact.placeholders.name')}
                  />
                </div>
                <div className={styles.formField}>
                  <label className={styles.label}>{t('landing.contact.labels.email')}</label>
                  <input
                    type="email"
                    className={styles.input}
                    placeholder={t('landing.contact.placeholders.email')}
                  />
                </div>
              </div>

              {/* Service-specific fields */}
              <div
                className={`${styles.serviceFields} ${
                  activeTab === 'service' ? styles.visible : styles.hidden
                }`}
              >
                <div className={styles.formRow}>
                  <div className={styles.formField}>
                    <label className={styles.label}>{t('landing.contact.labels.company')}</label>
                    <input type="text" className={styles.input} />
                  </div>
                  <div className={styles.formField}>
                    <label className={styles.label}>{t('landing.contact.labels.volume')}</label>
                    <select className={styles.select}>
                      {t('landing.contact.options')
                        .split(',')
                        .map((option: string, i: number) => (
                          <option key={i}>{option}</option>
                        ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className={styles.formField}>
                <label className={styles.label}>{t('landing.contact.labels.subject')}</label>
                <input
                  type="text"
                  className={styles.input}
                  placeholder={
                    activeTab === 'service'
                      ? t('landing.contact.placeholders.subjectService')
                      : t('landing.contact.placeholders.subjectSupport')
                  }
                />
              </div>

              <div className={styles.formField}>
                <label className={styles.label}>{t('landing.contact.labels.message')}</label>
                <textarea
                  rows={5}
                  className={styles.textarea}
                  placeholder={t('landing.contact.placeholders.message')}
                ></textarea>
              </div>

              <div className={styles.submitArea}>
                <button type="button" className={styles.submitButton}>
                  {t('landing.contact.labels.send')}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
